import React, { useState } from 'react';
import { Link2, Search, UserCircle, X } from 'lucide-react';
import type { Ticket } from '../../types';
import { Button, Modal } from '../../shared/ui';
import { demandeConfirmationAdresse, messageErreurRattachement, rattacherDossier, rechercheInitiale } from './rattachement';
import { assurerCompteSav, messageErreurCompteSav, noteValidation, useRechercheClients, type ClientChoisi } from './rechercheClients';
import { ListeResultatsClients } from './ListeResultatsClients';

// Mobile et tablette : cibles tactiles d'au moins 40 px, boutons de validation
// empilés pleine largeur sur téléphone. À partir de sm / lg, rendu d'origine.
const TACTILE = 'min-h-[40px] lg:min-h-0';
// Sur téléphone, les boutons restent collés en bas de la fenêtre qui défile.
const ACTIONS_FENETRE = 'flex flex-col gap-2 sm:flex-row sm:justify-end max-sm:sticky max-sm:-bottom-5 max-sm:z-10 max-sm:bg-white max-sm:-mx-5 max-sm:px-5 max-sm:pb-5 max-sm:pt-3 max-sm:-mb-5';
const BOUTON_FENETRE = `w-full sm:w-auto ${TACTILE}`;

// 16 px sur téléphone : pas de zoom automatique d'iOS au focus.
const champ = 'block w-full rounded-lg border border-trait bg-white py-2 pl-9 pr-3 text-base sm:text-sm text-encre placeholder:text-graphite/70 focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none';

/**
 * Rattache un dossier sans compte client à un compte SAV, ou à un client de
 * klygroupe.com (décision du 16/10/2026) : choisir ce dernier ne crée rien,
 * son compte SAV est créé ou vérifié par le serveur au clic sur
 * « Rattacher », juste avant le rattachement, qui part avec le compte rendu
 * par le serveur. Si cette création vient de rattacher d'office ce dossier
 * (même adresse, commande validée), le serveur répond qu'il l'est déjà à ce
 * compte : c'est un succès. La recherche interroge en parallèle les comptes SAV et la
 * boutique (rechercheClients.ts, comme à la création d'un dossier) ; rien
 * n'est choisi d'office, même si l'adresse du contact correspond : c'est
 * l'encadrement qui confirme, et le serveur garde son contrôle d'adresse
 * (ADRESSE_DIFFERENTE). Le dossier relu est rendu à `onRattache` (null si la
 * relecture a échoué).
 */
export const RattacherClientModal: React.FC<{
  ticket: Pick<Ticket, 'id' | 'contactEmail' | 'contactName'>;
  onClose: () => void;
  onRattache: (dossier: Ticket | null) => void;
}> = ({ ticket, onClose, onRattache }) => {
  const [recherche, setRecherche] = useState(() => rechercheInitiale(ticket));
  const [client, setClient] = useState<ClientChoisi | null>(null);
  const etatRecherche = useRechercheClients(recherche, !client);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const choisir = (c: ClientChoisi) => {
    setClient(c);
    setErreur(null);
  };

  const rattacher = async () => {
    if (!client) return;
    setEnvoi(true);
    setErreur(null);
    try {
      let compte;
      try {
        compte = await assurerCompteSav(client);
      } catch (e) {
        setErreur(messageErreurCompteSav(e));
        return;
      }
      // Compte établi : un nouvel essai ne le redemande pas au serveur.
      setClient(compte);
      let dossier;
      try {
        dossier = await rattacherDossier(ticket.id, compte.id);
      } catch (e) {
        // Adresse du dossier différente de celle du compte : on confirme d'abord.
        if (!demandeConfirmationAdresse(e)) throw e;
        if (!window.confirm(e.message)) return;
        dossier = await rattacherDossier(ticket.id, compte.id, true);
      }
      onRattache(dossier);
    } catch (e) {
      setErreur(messageErreurRattachement(e));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Rattacher à un compte client" size="md">
      <div className="space-y-4">
        <p className="text-sm text-graphite">
          Le dossier sera visible par ce client dans son espace, avec les messages qui ne sont pas des notes internes.
          Vérifiez qu'il s'agit bien de la même personne ou de la même société.
        </p>

        {client ? (
          <div className="flex items-center gap-3 rounded-lg border border-trait bg-brume px-3 py-2">
            <UserCircle className="w-5 h-5 text-graphite shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-encre truncate">{client.displayName}</p>
              <p className="text-xs text-graphite truncate">{client.email}{client.phone ? ` · ${client.phone}` : ''}{client.customerCode ? ` · Code SAGE ${client.customerCode}` : ''}</p>
              {noteValidation(client) && <p className="text-xs text-sapin-700">{noteValidation(client)}</p>}
            </div>
            <button type="button" onClick={() => { setClient(null); setErreur(null); }} aria-label="Changer de compte" className="p-3 -m-2 lg:p-1 lg:m-0 text-graphite hover:text-encre" disabled={envoi}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-graphite pointer-events-none" />
              <input
                value={recherche}
                onChange={e => setRecherche(e.target.value)}
                placeholder="Société, nom, e-mail, téléphone ou n° de compte du client"
                className={champ}
                aria-label="Rechercher un compte client"
                autoFocus
              />
            </div>
            <ListeResultatsClients
              recherche={recherche}
              etat={etatRecherche}
              onChoisi={choisir}
              messageVide="Aucun client trouvé dans le SAV ni sur klygroupe.com. Vérifiez l'orthographe, ou cherchez par e-mail, société ou numéro de compte."
            />
          </div>
        )}

        {erreur && <p role="alert" className="text-sm text-red-700">{erreur}</p>}

        <div data-testid="actions-fenetre" className={`${ACTIONS_FENETRE} pt-2 border-t border-trait`}>
          <Button variant="secondary" onClick={onClose} disabled={envoi} className={BOUTON_FENETRE}>Annuler</Button>
          <Button variant="primary" onClick={rattacher} disabled={envoi || !client} className={BOUTON_FENETRE}>
            <Link2 className="w-4 h-4 mr-1" />{envoi ? 'Rattachement…' : 'Rattacher'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RattacherClientModal;
